import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';
import { DateTimePicker } from '@/components/DateTimePicker';
import type { ComponentPageData } from '@/features/content/useComponent';

import { useUpdateComponentSettings } from './hooks/useUpdateComponentSettings';
import styles from './VisibilitySettingsDialog.module.css';

interface VisibilitySettingsDialogProps {
  component: ComponentPageData;
  componentId: string;
  courseId: string;
  onOpenChange(nextOpen: boolean): void;
  open: boolean;
}

function getTimestamp(value: string | undefined, fallback = Date.now()) {
  const parsed = value ? Number(value) : fallback;
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatTimestamp(timestamp: number) {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(timestamp));
}

export function VisibilitySettingsDialog({
  component,
  componentId,
  courseId,
  onOpenChange,
  open,
}: VisibilitySettingsDialogProps) {
  const mutation = useUpdateComponentSettings();
  const [contentStart, setContentStart] = useState(() =>
    getTimestamp(component.controlPanel.contentStartTimeUtcMilli),
  );
  const [homeworkSync, setHomeworkSync] = useState(true);
  const [homeworkStart, setHomeworkStart] = useState(() =>
    getTimestamp(component.controlPanel.homeworkStartTimeUtcMilli),
  );
  const [preLectureSync, setPreLectureSync] = useState(true);
  const [preLectureStart, setPreLectureStart] = useState(() =>
    getTimestamp(component.controlPanel.preLectureStartTimeUtcMilli),
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextContentStart = getTimestamp(
      component.controlPanel.contentStartTimeUtcMilli,
    );
    const nextHomeworkStart = getTimestamp(
      component.controlPanel.homeworkStartTimeUtcMilli,
      nextContentStart,
    );
    const nextPreLectureStart = getTimestamp(
      component.controlPanel.preLectureStartTimeUtcMilli,
      nextContentStart,
    );

    setContentStart(nextContentStart);
    setHomeworkStart(nextHomeworkStart);
    setPreLectureStart(nextPreLectureStart);
    setHomeworkSync(
      !component.controlPanel.homeworkStartTimeUtcMilli ||
        component.controlPanel.homeworkStartTimeUtcMilli ===
          component.controlPanel.contentStartTimeUtcMilli,
    );
    setPreLectureSync(
      !component.controlPanel.preLectureStartTimeUtcMilli ||
        component.controlPanel.preLectureStartTimeUtcMilli ===
          component.controlPanel.contentStartTimeUtcMilli,
    );
  }, [component, open]);

  const homeworkVisible =
    component.controlPanel.hasHomeworkQuestionSection &&
    Boolean(component.controlPanel.homeworkStartTimeUtcMilli) &&
    Number(component.controlPanel.homeworkStartTimeUtcMilli) <= Date.now();
  const preLectureVisible =
    component.controlPanel.hasPreLectureQuestionSection &&
    Boolean(component.controlPanel.preLectureStartTimeUtcMilli) &&
    Number(component.controlPanel.preLectureStartTimeUtcMilli) <= Date.now();

  async function handleSave() {
    await mutation.mutateAsync({
      applyChanges: (draft) => {
        const serializedContentStart = String(contentStart);
        const serializedHomeworkStart = String(homeworkSync ? contentStart : homeworkStart);
        const serializedPreLectureStart = String(
          preLectureSync ? contentStart : preLectureStart,
        );

        draft.content_start_time_utc_milli = serializedContentStart;
        draft.contentStartTimeUtcMilli = serializedContentStart;
        draft.start_time_utc_milli = serializedHomeworkStart;
        draft.startTimeUtcMilli = serializedHomeworkStart;
        draft.pre_lecture_start_time_utc_milli = serializedPreLectureStart;
        draft.preLectureStartTimeUtcMilli = serializedPreLectureStart;
        draft.is_live = true;
        draft.isLive = true;
      },
      component,
      componentId,
      courseId,
    });

    onOpenChange(false);
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div className={styles.header}>
            <Dialog.Title className={styles.title}>Visibility Settings</Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.warning}>
                Note: once homework or pre-lecture questions are visible to students
                you can NOT adjust which questions are assigned.
              </p>
            </Dialog.Description>
          </div>

          <div className={styles.body}>
            <DateTimePicker
              id="content-start"
              label="Content start time"
              onChange={(nextTimestamp) => {
                if (nextTimestamp !== null) {
                  setContentStart(nextTimestamp);
                }
              }}
              value={contentStart}
            />

            {component.controlPanel.hasPreLectureQuestionSection ? (
              <label className={styles.checkboxLabel}>
                <input
                  checked={preLectureSync}
                  disabled={preLectureVisible}
                  onChange={(event) => setPreLectureSync(event.target.checked)}
                  type="checkbox"
                />
                <span>
                  Make pre-lecture questions visible at the same time as section
                  text and image content
                </span>
              </label>
            ) : null}

            {component.controlPanel.hasPreLectureQuestionSection && !preLectureSync ? (
              <DateTimePicker
                id="pre-lecture-start"
                label="Pre-lecture visibility time"
                onChange={(nextTimestamp) => {
                  if (nextTimestamp !== null) {
                    setPreLectureStart(nextTimestamp);
                  }
                }}
                value={preLectureStart}
              />
            ) : null}

            {component.controlPanel.hasHomeworkQuestionSection ? (
              <label className={styles.checkboxLabel}>
                <input
                  checked={homeworkSync}
                  disabled={homeworkVisible}
                  onChange={(event) => setHomeworkSync(event.target.checked)}
                  type="checkbox"
                />
                <span>
                  Make homework questions visible at the same time as section text
                  and image content
                </span>
              </label>
            ) : null}

            {component.controlPanel.hasHomeworkQuestionSection && !homeworkSync ? (
              <DateTimePicker
                id="homework-start"
                label="Homework visibility time"
                onChange={(nextTimestamp) => {
                  if (nextTimestamp !== null) {
                    setHomeworkStart(nextTimestamp);
                  }
                }}
                value={homeworkStart}
              />
            ) : null}

            <ul className={styles.summaryList}>
              <li>Section content visible: {formatTimestamp(contentStart)}</li>
              {component.controlPanel.hasHomeworkQuestionSection ? (
                <li>
                  Homework visible:{' '}
                  {formatTimestamp(homeworkSync ? contentStart : homeworkStart)}
                </li>
              ) : null}
              {component.controlPanel.hasPreLectureQuestionSection ? (
                <li>
                  Pre-lecture visible:{' '}
                  {formatTimestamp(preLectureSync ? contentStart : preLectureStart)}
                </li>
              ) : null}
            </ul>
          </div>

          {mutation.isError ? (
            <p aria-live="polite" className={styles.error} role="alert">
              {mutation.error.message}
            </p>
          ) : null}

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button className={styles.cancelButton} variant="secondary">
                CANCEL
              </Button>
            </Dialog.Close>
            <Button
              className={styles.saveButton}
              disabled={mutation.isPending}
              onClick={() => {
                void handleSave();
              }}
              variant="primary"
            >
              {mutation.isPending ? 'Saving…' : 'SAVE'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
